import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { 
    iniciarJornada, 
    pausarJornada, 
    reanudarJornada, 
    finalizarJornada, 
    getEstadoJornada, 
    getHistorialJornadas,
    eliminarRegistroControlHorario
} from '../../../actions/controlHorario';
import { LockIcon, TrashIcon } from '../../../assets/svg/iconos';

const timezone = process.env.REACT_APP_TIMEZONE || 'Europe/Madrid';

export const ControlHorarios = ({ objProfesional }) => {
    const dispatch = useDispatch();
    const { colores } = useSelector(state => state.ui);
    const { empresa: empresaAuth, profesional: authProfesional } = useSelector(state => state.auth);
    const empresa = typeof empresaAuth === 'string' ? empresaAuth : empresaAuth?._id;
    
    const esPropietario = (authProfesional && authProfesional === objProfesional._id) || 
                          (!authProfesional && objProfesional.usuario);

    const [estadoJornada, setEstadoJornada] = useState(null);
    const [ultimaAccion, setUltimaAccion] = useState(null);
    const [tiempoTranscurridoSegundos, setTiempoTranscurridoSegundos] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mostrarHistorial, setMostrarHistorial] = useState(false);
    const [limiteActual, setLimiteActual] = useState(100);
    const [totalJornadas, setTotalJornadas] = useState(0);
    
    // Modal de contraseña
    const [mostrarModalPassword, setMostrarModalPassword] = useState(false);
    const [accionPendiente, setAccionPendiente] = useState(null);
    const [password, setPassword] = useState('');
    
    const [mostrarModalBorrar, setMostrarModalBorrar] = useState(false);
    const [registroABorrar, setRegistroABorrar] = useState(null);

    useEffect(() => {
        if (objProfesional?._id) {
            cargarEstadoJornada();
        }
    }, [objProfesional?._id]);

    const cargarEstadoJornada = async () => {
        try {
            const resultado = await dispatch(getEstadoJornada(objProfesional._id, empresa));
            if (resultado.ok) {
                setEstadoJornada({
                    estado: resultado.estado,
                    ultimaAccion: resultado.ultimaAccion
                });
                setUltimaAccion(resultado.ultimaAccion);
                setTiempoTranscurridoSegundos(resultado.tiempoTranscurrido);
            }
        } catch (error) {
            console.log('Error al cargar estado:', error);
        }
    };

    const cargarHistorial = async (limite = null) => {
        try {
            setLoading(true);
            const limiteAUsar = limite !== null ? limite : limiteActual;
            if (limite !== null) {
                setLimiteActual(limite);
            }
            const resultado = await dispatch(getHistorialJornadas(objProfesional._id, empresa, null, null, limiteAUsar));
            
            if (resultado.ok) {
                setHistorial(resultado.jornadas || []);
                if (resultado.totalJornadas) {
                    setTotalJornadas(resultado.totalJornadas);
                }
                setMostrarHistorial(true);
            }
        } catch (error) {
            console.log('Error al cargar historial:', error);
        } finally {
            setLoading(false);
        }
    };

    const cargarMas = () => {
        const nuevoLimite = limiteActual + 100;
        cargarHistorial(nuevoLimite);
    };

    const solicitarAccion = (accion) => {
        setAccionPendiente(accion);
        setMostrarModalPassword(true);
        setPassword('');
    };

    const handleConfirmarPassword = async () => {
        if (!password) {
            return;
        }

        setLoading(true);
        let resultado;

        try {
            switch (accionPendiente) {
                case 'iniciar':
                    resultado = await dispatch(iniciarJornada(objProfesional._id, password, empresa));
                    break;
                case 'pausar':
                    resultado = await dispatch(pausarJornada(objProfesional._id, password, empresa));
                    break;
                case 'reanudar':
                    resultado = await dispatch(reanudarJornada(objProfesional._id, password, empresa));
                    break;
                case 'finalizar':
                    resultado = await dispatch(finalizarJornada(objProfesional._id, password, empresa));
                    break;
                default:
                    return;
            }

            if (resultado.ok) {
                setMostrarModalPassword(false);
                setPassword('');
                setAccionPendiente(null);
                await cargarEstadoJornada();
            }
        } catch (error) {
            console.log('Error en acción:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelarPassword = () => {
        setMostrarModalPassword(false);
        setPassword('');
        setAccionPendiente(null);
    };

    const solicitarBorrarRegistro = (registro) => {
        setRegistroABorrar(registro);
        setMostrarModalBorrar(true);
    };

    const handleConfirmarBorrado = async () => {
        if (!registroABorrar) return;

        setLoading(true);
        try {
            const resultado = await dispatch(eliminarRegistroControlHorario(registroABorrar._id, empresa));
            
            if (resultado.ok) {
                setMostrarModalBorrar(false);
                setRegistroABorrar(null);
                await cargarHistorial();
                await cargarEstadoJornada();
            }
        } catch (error) {
            console.log('Error al borrar registro:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelarBorrado = () => {
        setMostrarModalBorrar(false);
        setRegistroABorrar(null);
    };

    const getEstadoTexto = () => {
        if (!estadoJornada || !estadoJornada.estado) return 'Sin jornada iniciada';
        
        switch (estadoJornada.estado) {
            case 'activa':
                return 'Jornada activa';
            case 'pausada':
                return 'Jornada pausada';
            case 'finalizada':
                return 'Jornada finalizada';
            case 'pendiente_finalizacion':
                return 'Pendiente de finalización';
            default:
                return 'Sin jornada iniciada';
        }
    };

    const getEstadoColor = () => {
        if (!estadoJornada || !estadoJornada.estado) return '#757575';
        
        switch (estadoJornada.estado) {
            case 'activa':
                return '#4CAF50';
            case 'pausada':
                return '#FF9800';
            case 'finalizada':
                return '#9E9E9E';
            case 'pendiente_finalizacion':
                return '#F44336';
            default:
                return '#757575';
        }
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return '';
        return moment(fecha).tz(timezone).format('DD/MM/YYYY HH:mm:ss');
    };

    const formatearFechaJornada = (fecha) => {
        if (!fecha) return '';
        
        const fechaMoment = moment(fecha).tz(timezone);
        
        if (!fechaMoment.isValid()) {
            return '';
        }
        
        const diasSemana = {
            'Monday': 'Lunes',
            'Tuesday': 'Martes',
            'Wednesday': 'Miércoles',
            'Thursday': 'Jueves',
            'Friday': 'Viernes',
            'Saturday': 'Sábado',
            'Sunday': 'Domingo'
        };
        
        const meses = {
            'January': 'enero',
            'February': 'febrero',
            'March': 'marzo',
            'April': 'abril',
            'May': 'mayo',
            'June': 'junio',
            'July': 'julio',
            'August': 'agosto',
            'September': 'septiembre',
            'October': 'octubre',
            'November': 'noviembre',
            'December': 'diciembre'
        };
        
        const diaSemana = fechaMoment.format('dddd'); // Monday, Tuesday, etc.
        const dia = fechaMoment.format('D'); // 1, 2, 3, etc.
        const mes = fechaMoment.format('MMMM'); // January, February, etc.
        const año = fechaMoment.format('YYYY'); // 2025, etc.
        
        const diaSemanaEsp = diasSemana[diaSemana] || diaSemana;
        const mesEsp = meses[mes] || mes;
        
        return `${diaSemanaEsp}, ${dia} de ${mesEsp} de ${año}`;
    };

    const parseUserAgent = (ua) => {
        if (!ua || ua === 'unknown' || ua === 'Sistema') return ua;
        
        if (ua.startsWith('Mobile ') || ua.startsWith('Tablet ') || ua.startsWith('Desktop ')) {
            return ua;
        }
        
        const isTablet = /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua);
        const isMobile = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua);
        
        let os = 'Unknown';
        if (/android/i.test(ua)) {
            os = 'Android';
        } else if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
            os = 'iOS';
        } else if (/win/i.test(ua)) {
            os = 'Windows';
        } else if (/mac/i.test(ua)) {
            os = 'Mac';
        } else if (/linux/i.test(ua)) {
            os = 'Linux';
        }
        
        let tipoDispositivo = '';
        if (isTablet) {
            if (os === 'iOS') {
                tipoDispositivo = 'Tablet iPad';
            } else if (os === 'Android') {
                tipoDispositivo = 'Tablet Android';
            } else {
                tipoDispositivo = `Tablet ${os}`;
            }
        } else if (isMobile) {
            if (os === 'iOS') {
                if (/iPhone/.test(ua)) {
                    tipoDispositivo = 'Mobile iPhone';
                } else if (/iPod/.test(ua)) {
                    tipoDispositivo = 'Mobile iPod';
                } else {
                    tipoDispositivo = 'Mobile iOS';
                }
            } else if (os === 'Android') {
                tipoDispositivo = 'Mobile Android';
            } else {
                tipoDispositivo = `Mobile ${os}`;
            }
        } else {
            tipoDispositivo = `Desktop ${os}`;
        }
        
        return tipoDispositivo;
    };

    const formatearTiempo = (segundos) => {
        if (!segundos && segundos !== 0) return '';
        const horas = Math.floor(segundos / 3600);
        const minutos = Math.floor((segundos % 3600) / 60);
        const segs = segundos % 60;
        return `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}:${segs.toString().padStart(2, '0')}`;
    };

    const [tiempoTranscurrido, setTiempoTranscurrido] = useState('');

    useEffect(() => {
        let interval;
        if (estadoJornada?.estado === 'activa' && tiempoTranscurridoSegundos !== null) {
            setTiempoTranscurrido(formatearTiempo(tiempoTranscurridoSegundos));
            
            interval = setInterval(() => {
                setTiempoTranscurridoSegundos(prev => {
                    const nuevo = (prev || 0) + 1;
                    setTiempoTranscurrido(formatearTiempo(nuevo));
                    return nuevo;
                });
            }, 1000);
        } else {
            setTiempoTranscurrido('');
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [estadoJornada?.estado, tiempoTranscurridoSegundos]);

    const getTituloAccion = () => {
        switch (accionPendiente) {
            case 'iniciar':
                return 'Iniciar jornada';
            case 'pausar':
                return 'Pausar jornada';
            case 'reanudar':
                return 'Reanudar jornada';
            case 'finalizar':
                return 'Finalizar jornada';
            default:
                return 'Confirmar acción';
        }
    };

    return (
        <div className='control-horarios'>
            {/* Card principal de estado y acción */}
            <div className='estado-card'>
                <div className='estado-header'>
                    <h3 style={{ color: colores.colorPrincipal }}>Estado actual</h3>
                    <div 
                        className='estado-badge' 
                        style={{ backgroundColor: getEstadoColor() }}
                    >
                        <span className='estado-icon'></span>
                        {getEstadoTexto()}
                    </div>
                </div>

                {ultimaAccion && (
                    <div className='estado-info' style={{ color: colores.colorPrincipal }}>
                        <div className='info-row'>
                            <strong>Última acción:</strong> 
                            <span>{formatearFecha(ultimaAccion.hora)}</span>
                        </div>
                        {estadoJornada?.estado === 'activa' && tiempoTranscurrido && (
                            <div className='tiempo-transcurrido' style={{ color: colores.colorPrincipal }}>
                                {tiempoTranscurrido}
                            </div>
                        )}
                    </div>
                )}

                <div className='accion-principal'>
                    {esPropietario ? (
                        <>
                            {(!estadoJornada || estadoJornada.estado === 'sin_jornada' || estadoJornada.estado === 'finalizada') && (
                                <button
                                    onClick={() => solicitarAccion('iniciar')}
                                    disabled={loading}
                                    className='btn-accion-principal'
                                    style={{ backgroundColor: colores.colorPrincipal }}
                                >
                                    {loading ? 'Procesando...' : 'Iniciar Jornada'}
                                </button>
                            )}

                            {estadoJornada && estadoJornada.estado === 'activa' && (
                                <div className='acciones-secundarias'>
                                    <button
                                        onClick={() => solicitarAccion('pausar')}
                                        disabled={loading}
                                        style={{ backgroundColor: colores.colorPrincipal }}
                                    >
                                        {loading ? 'Procesando...' : 'Pausar'}
                                    </button>
                                    <button
                                        onClick={() => solicitarAccion('finalizar')}
                                        disabled={loading}
                                        style={{ backgroundColor: colores.colorPrincipal }}
                                    >
                                        {loading ? 'Procesando...' : 'Finalizar'}
                                    </button>
                                </div>
                            )}

                            {estadoJornada && estadoJornada.estado === 'pausada' && (
                                <div className='acciones-secundarias'>
                                    <button
                                        onClick={() => solicitarAccion('reanudar')}
                                        disabled={loading}
                                        style={{ backgroundColor: colores.colorPrincipal }}
                                    >
                                        {loading ? 'Procesando...' : 'Reanudar'}
                                    </button>
                                    <button
                                        onClick={() => solicitarAccion('finalizar')}
                                        disabled={loading}
                                        style={{ backgroundColor: colores.colorPrincipal }}
                                    >
                                        {loading ? 'Procesando...' : 'Finalizar'}
                                    </button>
                                </div>
                            )}

                            {estadoJornada && estadoJornada.estado === 'pendiente_finalizacion' && (
                                <button
                                    onClick={() => solicitarAccion('finalizar')}
                                    disabled={loading}
                                    className='btn-accion-principal'
                                    style={{ backgroundColor: colores.colorPrincipal }}
                                >
                                    {loading ? 'Procesando...' : 'Finalizar Jornada Pendiente'}
                                </button>
                            )}
                        </>
                    ) : (
                        <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            backgroundColor: colores.color6 || '#f5f5f5',
                            borderRadius: '8px',
                            color: colores.colorPrincipal,
                            width: '80%',
                            margin: 'auto auto 15px'
                        }}>
                            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center', transform: 'scale(1.3)' }}>
                                <LockIcon color={colores.colorPrincipal} />
                            </div>
                            <p style={{ margin: '0', fontSize: '14px', fontWeight: '500' }}>
                                Solo {objProfesional.nombre} puede gestionar su jornada
                            </p>
                            <p style={{ margin: '5px 0 0 0', fontSize: '12px', opacity: '0.7' }}>
                                Vista de solo lectura
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Historial */}
            <div className='historial-section'>
                <div className='historial-header'>
                    <h3 style={{ color: colores.colorPrincipal }}>Historial de jornadas</h3>
                    <button
                        onClick={mostrarHistorial ? () => {
                            setMostrarHistorial(false);
                            setLimiteActual(100);
                        } : () => {
                            setLimiteActual(100);
                            cargarHistorial(100);
                        }}
                        className='btn-toggle-historial'
                        style={{ backgroundColor: colores.colorPrincipal }}
                    >
                        {mostrarHistorial ? 'Ocultar' : 'Ver Historial'}
                    </button>
                </div>

                {mostrarHistorial && (
                    <div className='historial-content'>
                        {loading ? (
                            <div className='historial-loading' style={{ color: colores.colorPrincipal }}>
                                Cargando historial...
                            </div>
                        ) : historial.length === 0 ? (
                            <div className='historial-vacio' style={{ color: colores.colorPrincipal }}>
                                No hay historial de jornadas
                            </div>
                        ) : (
                            <>
                                <div className='historial-lista'>
                                    {historial.map((jornada, index) => {
                                        const fechaJornada = jornada.acciones && jornada.acciones.length > 0 
                                            ? jornada.acciones[0].hora 
                                            : null;
                                        return (
                                        <div key={jornada.jornadaId || index} className='jornada-item' data-jornada-id={jornada.jornadaId}>
                                            <div className='jornada-header' style={{ color: colores.colorPrincipal }}>
                                            Jornada #{totalJornadas - index}: {formatearFechaJornada(fechaJornada)}
                                            </div>
                                            <div className='jornada-acciones'>
                                                {jornada.acciones && jornada.acciones.length > 0 ? (
                                                    jornada.acciones.map((accion, accionIndex) => (
                                                        <div key={accionIndex} className='accion-item'>
                                                            <span className={`accion-tipo ${accion.accion}`}>
                                                                {accion.accion}
                                                            </span>
                                                            <div className='accion-detalles' style={{ color: colores.colorPrincipal }}>
                                                                <div className='accion-fecha'>
                                                                    {formatearFecha(accion.hora)}
                                                                </div>
                                                                <div className='accion-dispositivo'>
                                                                    {parseUserAgent(accion.dispositivo)}
                                                                </div>
                                                            </div>
                                                            {!esPropietario && (
                                                                <button
                                                                    className='btn-borrar-registro'
                                                                    onClick={() => solicitarBorrarRegistro(accion)}
                                                                    title="Eliminar registro"
                                                                >
                                                                    <TrashIcon color="#757575" width="14" height="14" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p style={{ color: colores.colorPrincipal }}>
                                                        No hay acciones registradas
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                                
                                {historial.length < totalJornadas && (
                                    <div className="historial-cargar-mas">
                                        <button
                                            onClick={cargarMas}
                                            disabled={loading}
                                            className="btn-cargar-mas"
                                            style={{ backgroundColor: colores.colorPrincipal }}
                                        >
                                            {loading ? 'Cargando...' : 'Cargar más jornadas'}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Modal de contraseña */}
            {mostrarModalPassword && (
                <div className='password-modal-overlay'>
                    <div className='password-modal'>
                        <div className='modal-header' style={{ color: colores.colorPrincipal }}>
                            <h3>{getTituloAccion()}</h3>
                            <p>Ingresa tu contraseña para confirmar la acción</p>
                        </div>
                        <div className='modal-body'>
                            <label style={{ color: colores.colorPrincipal }}>Contraseña:</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleConfirmarPassword()}
                                placeholder="Ingrese su contraseña"
                                autoFocus
                            />
                        </div>
                        <div className='modal-footer'>
                            <button
                                onClick={handleCancelarPassword}
                                className='btn-cancelar'
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirmarPassword}
                                className='btn-confirmar'
                                style={{ backgroundColor: colores.colorPrincipal }}
                                disabled={loading || !password}
                            >
                                {loading ? 'Procesando...' : 'Confirmar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {mostrarModalBorrar && registroABorrar && (
                <div className='password-modal-overlay'>
                    <div className='password-modal'>
                        <div className='modal-header' style={{ color: colores.colorPrincipal }}>
                            <h3>Eliminar registro</h3>
                            <p>¿Estás seguro de que deseas eliminar este registro?</p>
                        </div>
                        <div className='modal-body'>
                            <div style={{ 
                                padding: '15px', 
                                backgroundColor: colores.color6 || '#f5f5f5', 
                                borderRadius: '4px',
                                marginBottom: '10px'
                            }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <strong>Acción:</strong> <span className={`accion-tipo ${registroABorrar.accion}`} style={{
                                        padding: '2px 8px',
                                        borderRadius: '3px',
                                        fontSize: '11px',
                                        marginLeft: '5px'
                                    }}>{registroABorrar.accion}</span>
                                </div>
                                <div style={{ fontSize: '13px', color: colores.colorPrincipal }}>
                                    <strong>Fecha:</strong> {formatearFecha(registroABorrar.hora)}
                                </div>
                            </div>
                            <p style={{ fontSize: '13px', color: '#e53935', margin: '10px 0 0 0' }}>
                                Esta acción no se puede deshacer.
                            </p>
                        </div>
                        <div className='modal-footer'>
                            <button
                                onClick={handleCancelarBorrado}
                                className='btn-cancelar'
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirmarBorrado}
                                className='btn-confirmar'
                                style={{ backgroundColor: '#e53935' }}
                                disabled={loading}
                            >
                                {loading ? 'Eliminando...' : 'Eliminar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
